import { getStateFromFips } from '@hyperobjekt/us-states'
import i18n from '@pureartisan/simple-i18n'
import { useEffect, useRef, useState } from 'react'
import { theme } from './theme'
import _ from 'lodash'
import { MAP_CONTROLS_CLASSES } from '../../../constants/map'
// const countyNames = require('./../../../constants/countyNames.json')
// const poi_data = require('../../../constants/poi_data.json')

const remoteData = {}
// load remote data needed by utils so that other util methods don't need to pass it in
export const setUtilsRemoteData = ({ data, id }) => {
  remoteData[id] = data
}

export const getActiveArray = index => {
  const arr = [0, 0, 0, 0, 0]
  arr[Math.round(index)] = 1
  return arr
}

/**
 * Loads map features based on a string of locations
 * @param {string} locations locations formed as `{id},{lat},{lon}` separated by a `+`
 * @returns {Promise<Array<Feature>>}
 */
export const loadFeaturesFromRoute = locations =>
  loadFeaturesFromCoords(parseLocationsString(locations))

/**
 * Loads map features from location parameter
 * @param {*} params
 * @returns {Promise<Array<Feature>>}
 */
export const loadFeaturesFromRouteParams = params =>
  params.locations
    ? loadFeaturesFromRoute(params.locations)
    : Promise.resolve([])

/**
 * Returns the feature with an id property that matches the
 * provided ID
 * @param {string} id
 * @param {FeatureCollection} collection
 * @returns {Feature}
 */
const getFeatureFromCollection = (id, collection) => {
  const feature = collection.find(
    f => f.properties.id === id,
  )
  if (!feature) {
    throw new Error(
      'feature ' + id + ' not found from tilequery API',
    )
  }
  return feature
}

/**
 * Converts string to title case
 * @param  String str String input
 * @return String
 */
export const toTitleCase = str => {
  str = str.toLowerCase().split(' ')
  for (var i = 0; i < str.length; i++) {
    str[i] =
      str[i].charAt(0).toUpperCase() + str[i].slice(1)
  }
  return str.join(' ')
}

/**
 * Returns a value rounded to the indicated number of decimals
 * @param  String value     Number or string, value passed to function
 * @param  Number decimals  Number of decimals to round to
 * @param  Boolan padZeroes If true, pad with extra zeroes to fill empty decimal spots
 * @return Number
 */
export const getRoundedValue = (
  value,
  decimals,
  padZeroes = false,
  isCurrency = false,
  isPercent = false,
) => {
  const type = typeof value
  if (!!isPercent) {
    value = value * 100
  }
  let fixed = null
  if (type === 'string') {
    if (padZeroes) {
      fixed = parseFloat(value)
        .toFixed(decimals)
        .toLocaleString()
    } else {
      fixed = +parseFloat(value)
        .toFixed(decimals)
        .toLocaleString()
    }
  } else {
    if (padZeroes) {
      fixed = Number(
        value.toFixed(decimals),
      ).toLocaleString()
    } else {
      fixed = Number(
        +value.toFixed(decimals),
      ).toLocaleString()
    }
  }
  if (!!isCurrency) {
    fixed = '$' + fixed
  }
  if (!!isPercent) {
    fixed = fixed + '%'
  }
  return fixed
}

/**
 * Calculates hash position (percent from left/0 based on min/max)
 * @param  Number value Value of metric
 * @param  Number min   Minimum of range for metric
 * @param  Number max   Maximum of range for metric
 * @return {[type]}       [description]
 */
export const getHashLeft = (value, min, max) => {
  return ((value - min) / (max - min)) * 100
}

/**
 * Returns string placeholder based on quintile provided
 * @param  Number quintile Quintile 0 - 4
 * @return String          String referncing translation file constant
 */
export const getQuintileDesc = quintile => {
  switch (true) {
    case quintile === 0: {
      return 'FIRST'
    }
    case quintile === 1: {
      return 'SECOND'
    }
    case quintile === 2: {
      return 'THIRD'
    }
    case quintile === 3: {
      return 'FOURTH'
    }
    case quintile === 4: {
      return 'FIFTH'
    }
  }
}

/**
 * Gets a variability level from a feature, 0-2
 */
export const getVaribilityLevel = ({
  activeNorm,
  metric,
  feature,
}) => {
  // keep in sync with "_VARIABILITY_TOOLTIP" lang strings
  const highCutoff = 40
  const someCutoff = 25
  const littleCutoff = 0

  // variability is calculated based on level data
  const levelRatingType = 'x'
  const key = `v${levelRatingType}${metric}${activeNorm}`
  // TODO: move ZIP/county var data to remoteJson?
  const variability =
    feature && feature.properties && feature.properties[key]

  if (!variability) return 0
  if (variability > highCutoff) return 3
  if (variability > someCutoff) return 2
  if (variability > littleCutoff) return 1
  return 0
}

/**
 * Gets a property from a feature, returns null if not found
 * @param {Feature} feature GeoJSON feature
 * @param {string} propName property name to grab
 */
export const getFeatureProperty = (feature, propName) => {
  if (
    feature &&
    feature.properties &&
    feature.properties[propName] !== -999
  ) {
    return feature.properties[propName]
  }
  return null
}

// Hook
export const usePrevious = value => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef()

  // Store current value in ref
  useEffect(() => {
    ref.current = value
  }, [value]) // Only re-run if value changes

  // Return previous value (happens before update in useEffect above)
  return ref.current
}

// https://dev.to/gabe_ragland/debouncing-with-react-hooks-jci
export const useDebounce = (value, delay) => {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(
    value,
  )

  useEffect(
    () => {
      // Set debouncedValue to value (passed in) after the specified delay
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)

      // Return a cleanup function that will be called every time ...
      // ... useEffect is re-called. useEffect will only be re-called ...
      // ... if value changes (see the inputs array below).
      // This is how we prevent debouncedValue from changing if value is ...
      // ... changed within the delay period. Timeout gets cleared and restarted.
      // To put it in context, if the user is typing within our app's ...
      // ... search box, we don't want the debouncedValue to update until ...
      // ... they've stopped typing for more than 500ms.
      return () => {
        clearTimeout(handler)
      }
    },
    // Only re-call effect if value changes
    // You could also add the "delay" var to inputs array if you ...
    // ... need to be able to change that dynamically.
    [value, delay],
  )

  return debouncedValue
}

export const getLevelArray = () => {
  return [
    i18n.translate(`LEVEL_VLOW`),
    i18n.translate(`LEVEL_LOW`),
    i18n.translate(`LEVEL_MOD`),
    i18n.translate(`LEVEL_HIGH`),
    i18n.translate(`LEVEL_VHIGH`),
  ]
}

export const getNormPhrase = activeNorm => {
  switch (activeNorm) {
    case 'n':
      return i18n.translate(`NATION`)
      break
    case 's':
      return i18n.translate(`STATE`)
      break
    case 'm':
      return i18n.translate(`METRO`)
      break
    default:
      return i18n.translate(`NATION`)
  }
}

export const getLongPopName = popCode => {
  return i18n
    .translate(`POP_LONG_${String(popCode).toUpperCase()}`)
    .indexOf('missing') < 0
    ? i18n.translate(
        `POP_LONG_${String(popCode).toUpperCase()}`,
      )
    : i18n.translate(`POP_${String(popCode).toUpperCase()}`)
}

export const getMedPopName = popCode => {
  return i18n
    .translate(`POP_MED_${String(popCode).toUpperCase()}`)
    .indexOf('missing') < 0
    ? i18n.translate(
        `POP_MED_${String(popCode).toUpperCase()}`,
      )
    : i18n.translate(`POP_${String(popCode).toUpperCase()}`)
}

export const capitalizeFirstLetter = string =>
  string.charAt(0).toUpperCase() + string.slice(1)

// => Census tract 138.00 or ZIP code 05342
export const getIdentifierPhrase = (
  geo,
  id,
  options = {},
) => {
  id = String(id)
  if (options.truncate && geo === 'tracts') {
    id = getFormattedTractId(id)
  } else if (geo === 'tracts') {
    id = id.padStart(11, 0)
  } else if (geo === 'zips') {
    id = id.padStart(5, 0)
  } else if (geo === 'counties') {
    return getCountyFromGeoid(id, options)
  }

  const phrase = `IDENTIFIER_PHRASE_${geo.toUpperCase()}`
  let output = i18n.translate(phrase, { id })

  if (options.sentenceCase) {
    output = output
      .split(' ')
      .map(capitalizeFirstLetter)
      .join(' ')
  }

  return output
}

/**
 * input id is fips, version is 'full' or 'abbr'
 * output is state name or null
 */
export const getStateNameFromFips = (
  id,
  version = 'full',
) => {
  const stateObj = getStateFromFips(
    String(id).padStart(2, '0').slice(0, 2),
  )
  return (stateObj && stateObj[version]) || null
}

/**
 * input id is metro fips id, metros are from remoteJson
 * output is metro name or null
 */
export const getMetroNameFromFips = id => {
  const metros = remoteData['metros']
  if (!metros) return ''

  const metro = metros.find(m => +m.GEOID === +id)
  return _.get(metro, 'name', '')
}

/**
 * input id is number of type SSCCCTTTTTT
 * output is string of type TTTT.TT with no leading 0s
 * where S = state, C = county, T = tract
 */
export const getFormattedTractId = id => {
  let strId = String(id)
  // states with a single digit id will be missing the leading 0
  if (strId.length === 10) strId = '0' + strId
  if (strId.length !== 11) {
    console.error('Unexpected tract id length: ', id)
    return strId
  }

  // remove SSCCC, as well as leading 0s from the TTTTTT portion
  strId = strId.slice(5).replace(/^0+/, '')
  // insert "."
  strId = strId.slice(0, -2) + '.' + strId.slice(-2)
  return strId
}

/**
 * input id is number of type SSCCCTTTTTT
 * output county display name (string)
 */
export const getCountyFromGeoid = (
  id,
  options = { withId: false },
) => {
  const county_names = remoteData['county_names']
  if (!county_names) return ''

  const strId = String(id)

  // tract ids are SSCCCTTTTTT (11 digits, or 10 if the state fips
  // starts with 0 and got stripped); county ids are SSCCC (5 digits,
  // or 4 with the same leading-0 issue).
  let countyId
  if (strId.length === 10 || strId.length === 11) {
    countyId = strId.padStart(11, '0').slice(0, 5)
  } else if (strId.length === 4 || strId.length === 5) {
    countyId = strId.padStart(5, '0')
  } else {
    console.warn('Unexpected geoid length: ', id)
    return ''
  }

  let countyName = county_names[countyId]

  if (options.withId) countyName += ` (${countyId})`
  return countyName
}

/**
 * Rounds a number in exponential notation to a whole number.
 * @param Number val Number in scientific notation
 * @returns Number
 */
export const formatExponent = val => {
  if (val < 1 && val > -1) {
    return Math.round(val).toPrecision(1)
  } else {
    return getRoundedValue(val, 0, true, false, false)
  }
}

// Get all parents of an element.
// https://gomakethings.com/how-to-get-all-parent-elements-with-vanilla-javascript/
export const getParents = function (elem) {
  // Set up a parent array
  var parents = []

  // Push each parent element to the array
  for (
    ;
    elem && elem !== document;
    elem = elem.parentNode
  ) {
    parents.push(elem)
  }

  // Return our parent array
  return parents
}

export const getIsControl = e => {
  const hovered = e.target
  // const hovered = document.querySelectorAll(':hover')
  const elements = [hovered, ...getParents(hovered)]
  // hovered.forEach(el => elements.push(getParents(el)))
  return Object.values(elements).some(
    node =>
      node &&
      MAP_CONTROLS_CLASSES.some(className =>
        node?.classList?.contains(className),
      ),
  )
}

/**
 * turns eg { a: 0, b: 0.13, c: 0.7, d: 0 } => [ { id: C, ... }, { id: B ... }]
 * @param {object} gradeMap mapping redlining grade to fraction, from redliningBreakdown
 * @returns {[object]}
 */
export const getSortedGrades = gradeMap => {
  const grades = ['A', 'B', 'C', 'D']
    .map(grade => {
      const val = parseFloat(gradeMap[grade.toLowerCase()])
      return (
        val && {
          id: grade,
          displayCount: val + '%',
          color: theme.extras.redlining[grade],
          name: 'Grade ' + grade,
          ratio: val / 100,
        }
      )
    })
    .filter(grade => !!grade)
    .sort((a, b) => b.ratio - a.ratio)

  // tracts with no recorded grades are considered 100% not redlined
  const notRedlinedVal = !grades.length
    ? 100
    : parseFloat(gradeMap['n'])
  notRedlinedVal &&
    grades.push({
      id: 'ungraded',
      displayCount: notRedlinedVal + '%',
      color: theme.extras.variables.colors.ungradedGray,
      name: 'Not rated',
      ratio: notRedlinedVal / 100,
    })
  return grades
}

/**
 * @param {string} str
 * @returns {boolean}
 */
export const isWkkfView = (str = '') =>
  str.startsWith('wkkf')

/**
 * @param {string} str
 * @returns {boolean}
 */
export const isExplorerView = str => {
  // the wkkf view is treated as an explorer
  return str === 'explorer' || isWkkfView(str)
}

/**
 * // when changing active POI, update active region/norm if necessary
 * @param  Number activePOIData
 * @param  String activeNorm
 * @returns {[object]}
 */
export const getStateUpdateForPOI = (id, activeNorm) => {
  const poi_data = remoteData['poi_data']
  if (!poi_data) return {}
  const activePOIData = poi_data.sites.find(
    s => s.id === id,
  )
  const values = {}

  if (activeNorm === 's') {
    // changing active POI in state mode, activate appropriate state
    values.activeRegion = activePOIData.state_fips
  } else if (activeNorm === 'm') {
    // changing active POI in metro mode, activate appropriate metro
    if (activePOIData.metro_mode) {
      values.activeRegion = activePOIData.metro_fips
    } else {
      // ...unless the POI doesn't belong to a metro, in which case change norm mode
      values.activeNorm = 'n'
      values.notification = 'NOTIFICATION_NORM_RESET'
    }
  }

  return values
}
